import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import BarraInferiorBotoes from '../../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../../components/BoxConteudo';
import CustomButton from '../../../../../../components/CustomButton';
import CustomCol from '../../../../../../components/CustomCol';
import CustomInputText from '../../../../../../components/CustomInputs/CustomInputText';
import CustomOffcanvas from '../../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../../components/CustomRow';
import CustomSelect from '../../../../../../components/CustomSelects/CustomSelect';
import CustomTable from '../../../../../../components/CustomTable';
import Log from '../../../../../../components/Log/Log';
import WorkspaceBotoes from '../../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../../components/WorkspaceFicha';
import {
  IProdutoOutroSistemaForm,
  IProdutoOutroSistemaRegistro,
} from '../../../../../../modules/types/ProdutoDaf';
import { useOutroSistema } from '../../../../../../services/enum/useOutroSistema';
import { getProdutoDAFOutroSistema } from '../../../../../../services/produtosDAF/produtosDafService';
import { functionLogData } from '../../../../../../utils/functionLogData';
import { handleSave } from '../../../../../../utils/handleSave';
import { headersOutrosSistemas } from '../../auxiliarProdutosDaf';
import { defaultProdutoOutroSistemaFormMain } from '../utils/formProdutoOutroSistemaDefault';

interface ProdutoOutrosSistemasProps {
  produtoId: string;
  userId: string;
}

const ProdutoOutrosSistemas: React.FC<ProdutoOutrosSistemasProps> = ({
  produtoId,
  userId,
}) => {
  const outroSistemaOptions = useOutroSistema();

  // Gatilho para atualização do Log
  const [refreshTriggerLog, setRefreshTriggerLog] = useState(0);
  const triggerRefresh = React.useCallback(() => {
    setRefreshTriggerLog((prev) => prev + 1);
  }, []);

  const [logData, setLogData] = useState<IProdutoOutroSistemaRegistro | null>(
    null,
  );

  // FORMULÁRIO
  const [dataRaw, setDataRaw] = useState<IProdutoOutroSistemaForm | null>(null);
  const [formMain, setFormMain] = useState<IProdutoOutroSistemaForm>(
    defaultProdutoOutroSistemaFormMain,
  );
  const [outroSistemaData, setOutroSistemaData] = useState<any[]>([]);

  const fetchData = React.useCallback(async (id: string | number) => {
    try {
      const response = await getProdutoDAFOutroSistema(id.toString());

      setOutroSistemaData(
        Array.isArray(response)
          ? response.filter((item) => item && item.id != null)
          : [],
      );
    } catch (error) {
      console.error('Erro ao obter dados:', error);
      setOutroSistemaData([]);
    }
  }, []);

  useEffect(() => {
    if (produtoId !== '') {
      fetchData(produtoId);
    }
  }, [produtoId, fetchData]);

  const theme = useTheme();

  const tableDataOutroSistema = useMemo(() => {
    if (!Array.isArray(outroSistemaData)) return [];

    return outroSistemaData
      .filter((item): item is Record<string, any> => Boolean(item))
      .map((item, index) => ({
        id: item.id ?? `temp-${produtoId}-${index}`, // 🔴 chave estável
        outroSistema:
          outroSistemaOptions[item.outroSistema]?.label ?? 'Não informado',
        codigoProduto: item.codigoProduto ?? '',
        nomeProduto: item.nomeProduto ?? '',
        __raw: item,
      }));
  }, [outroSistemaData, outroSistemaOptions, produtoId]);

  //Controlar offcanvas
  const [show, setShow] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const onNovoRegistro = React.useCallback(() => {
    const novoRegistro: IProdutoOutroSistemaForm = {
      ...defaultProdutoOutroSistemaFormMain,
      produtoDAFId: Number(produtoId),
      usuarioId: Number(userId),
    };

    setFormMain(novoRegistro);
    setDataRaw(novoRegistro);
    setLogData(null); // novo registro não tem log
    setSaveDisabled(false);
    setShow(true);
  }, [produtoId, userId]);
  const onClose = React.useCallback(() => {
    setShow(false);
  }, []);

  const handleChange = React.useCallback(
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) => {
      const { name, value } = event.target;

      setFormMain((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [],
  );

  const logs = functionLogData(logData);

  const camposObrigatorios: Record<string, string> = {
    outroSistema: 'Sistema',
    codigoProduto: 'Código do Produto',
    nomeProduto: 'Nome do Produto',
  };

  const camposAjustar = {
    number: ['id', 'usuarioId', 'outroSistema'],
    string: ['codigoProduto', 'nomeProduto', 'observacoes'],
    date: ['dataCriacao', 'dataExtincao'],
  };

  const salvar = React.useCallback(() => {
    handleSave(
      formMain,
      camposObrigatorios,
      camposAjustar,
      'produtooutrosistema',
      'daf/produtosdaf/produtos/produto',
      undefined,
      false,
      dataRaw,
    );
    fetchData(Number(produtoId));
    triggerRefresh();
  }, [formMain, camposObrigatorios, camposAjustar, fetchData, dataRaw]);

  const handleRowClick = React.useCallback((row: any) => {
    if (!row?.__raw) return;

    const registro = row.__raw as IProdutoOutroSistemaRegistro;

    setFormMain({
      ...defaultProdutoOutroSistemaFormMain,
      ...registro,
    });

    setDataRaw(registro);
    setLogData(registro);
    setSaveDisabled(false);
    setShow(true);
  }, []);

  return (
    <BoxConteudo boxTitulo="Outros Sistemas">
      <CustomCol width="100%">
        {tableDataOutroSistema.length > 0 && (
          <CustomTable
            headers={headersOutrosSistemas}
            data={tableDataOutroSistema}
            showPagination={false}
            onRowClick={(row) => handleRowClick(row)}
          />
        )}
      </CustomCol>
      <CustomCol style={{ marginTop: '15px', textAlign: 'right' }}>
        <CustomButton
          label="Novo registro"
          bgColor={theme.colors.botaoAcao.azul}
          textColor={theme.colors.white}
          onClick={onNovoRegistro}
        />
      </CustomCol>

      <CustomOffcanvas
        show={show}
        onClose={onClose}
        title="Outro Sistema"
        width={'60vw'}
        placement="end"
        footerContent={
          <BarraInferiorBotoes>
            <WorkspaceBotoes
              save
              saveDisabled={saveDisabled}
              onClickSave={salvar}
            />
          </BarraInferiorBotoes>
        }
      >
        <WorkspaceFicha>
          <Log
            {...logs}
            entity="TB_PRODUTO_OUTRO_SISTEMA"
            id={Number(formMain.id)}
            refreshTriggerLog={refreshTriggerLog}
          />
          <BoxConteudo boxTitulo="Dados da Produto no Outro Sistema">
            <CustomRow style={{ marginTop: '-15px' }}>
              <CustomCol width="200px">
                <CustomSelect
                  name="outroSistema"
                  options={outroSistemaOptions}
                  title="Sistema"
                  value={formMain.outroSistema}
                  onChange={handleChange}
                  // style={statusIncorporacaoStyle}
                  required
                />
              </CustomCol>
              <CustomCol width="200px">
                <CustomInputText
                  name="codigoProduto"
                  title="Código do Produto"
                  value={formMain.codigoProduto}
                  id="codigoProduto"
                  onChange={handleChange}
                  required
                />
              </CustomCol>
              <CustomCol>
                <CustomInputText
                  name="nomeProduto"
                  title="Nome do Produto"
                  value={formMain.nomeProduto}
                  id="nomeProduto"
                  onChange={handleChange}
                  required
                />
              </CustomCol>
            </CustomRow>

            <CustomRow>
              <CustomCol width="100%">
                <CustomInputText
                  id="observacoes"
                  name="observacoes"
                  value={formMain.observacoes}
                  title="Observações"
                  type="textArea"
                  onChange={handleChange}
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </BoxConteudo>
  );
};

export default ProdutoOutrosSistemas;
